import angular = require("angular");
import * as moment from 'moment';
import * as XLSX from 'xlsx';
import { FeesRepo } from "@services/fees.repo";

type DashboardMode = 'client' | 'portfolio';

interface ModeConfig {
    storagePrefix: string;
    cardTitle: string;
    cardIcon: string;
    detailTableStoragePrefix: string;
    exportSheetName: string;
    exportSummarySheetName: string;
    exportFilePrefix: string;
    summaryExportSheetName: string;
    summaryExportFilePrefix: string;
}

const MODE_CONFIGS: Record<DashboardMode, ModeConfig> = {
    client: {
        storagePrefix: 'clientActivity',
        cardTitle: 'Client Activity',
        cardIcon: 'people',
        detailTableStoragePrefix: 'clientActivity',
        exportSheetName: 'Client Activity',
        exportSummarySheetName: 'Client Summary',
        exportFilePrefix: 'client_activity',
        summaryExportSheetName: 'Client Summary',
        summaryExportFilePrefix: 'client_summary',
    },
    portfolio: {
        storagePrefix: 'portfolioSummary',
        cardTitle: 'Portfolio Activity',
        cardIcon: 'person_add',
        detailTableStoragePrefix: 'portfolioActivity',
        exportSheetName: 'Portfolio Activity',
        exportSummarySheetName: 'Portfolio Summary',
        exportFilePrefix: 'portfolio_activity',
        summaryExportSheetName: 'Portfolio Summary',
        summaryExportFilePrefix: 'portfolio_summary',
    }
};

class ActivityDashboardController implements angular.IOnInit {
    public mode: DashboardMode;

    public fromDate: Date;
    public toDate: Date;
    public selectedFromDate: Date;
    public selectedToDate: Date;
    public currentDate: Date;

    public summaryRefreshKey: number = 0;
    public isEDR: boolean = false;

    public summaryLabels: any[];
    public summaryFetchData: (args: { fromDate: Date; toDate: Date }) => angular.IPromise<any>;

    public detailColumns: any[];
    public detailStatusOptions: any[];
    public detailFetchData: (args: { fromDate: Date; toDate: Date }) => angular.IPromise<any>;
    public detailMapData: (args: { results: any[]; ctrl: any }) => any[];

    private lastSummaryData: any = null;
    private lastDetailResults: any[] = [];
    private loadingCount: number = 0;

    get isLoading(): boolean {
        return this.loadingCount > 0;
    }

    get isPortfolio(): boolean {
        return this.mode === 'portfolio';
    }

    get config(): ModeConfig {
        return MODE_CONFIGS[this.mode] || MODE_CONFIGS.client;
    }

    /** @ngInject */
    constructor(
        private feesRepo: FeesRepo,
        private notifyjs: any,
    ) {}

    $onInit() {
        this.currentDate = new Date();
        const prefix = this.config.storagePrefix;

        const savedFromDate = localStorage.getItem(prefix + 'FromDate');
        const savedToDate = localStorage.getItem(prefix + 'ToDate');

        if (savedFromDate) {
            this.selectedFromDate = new Date(savedFromDate);
        } else {
            this.selectedFromDate = new Date(moment(this.currentDate).subtract(1, 'month').startOf('month').format('YYYY-MM-DD'));
        }

        if (savedToDate) {
            this.selectedToDate = new Date(savedToDate);
        } else {
            this.selectedToDate = new Date(moment(this.currentDate).subtract(1, 'month').endOf('month').format('YYYY-MM-DD'));
        }

        if (this.isPortfolio) {
            const savedIsEDR = localStorage.getItem(prefix + 'IsEDR');
            if (savedIsEDR !== null) {
                this.isEDR = savedIsEDR === 'true';
            }
        }

        this.fromDate = this.selectedFromDate ? new Date(this.selectedFromDate.getTime()) : null;
        this.toDate = this.selectedToDate ? new Date(this.selectedToDate.getTime()) : null;

        this.summaryLabels = this.buildSummaryLabels();
        this.summaryFetchData = this.buildSummaryFetchData();
        this.detailColumns = this.buildDetailColumns();
        this.detailStatusOptions = this.buildDetailStatusOptions();
        this.detailFetchData = this.buildDetailFetchData();
        this.detailMapData = this.buildDetailMapData();

        if (this.fromDate && this.toDate) {
            this.summaryRefreshKey++;
        }
    }

    formatDate(date: Date) {
        return moment(date).format('YYYY-MM-DD');
    }

    getAccountSource(): string {
        return this.isEDR ? "Rothschild" : "MCB";
    }

    handleSearch() {
        if (!this.selectedFromDate || !this.selectedToDate) {
            return;
        }
        if (this.selectedFromDate > this.selectedToDate) {
            return;
        }

        this.fromDate = new Date(this.selectedFromDate.getTime());
        this.toDate = new Date(this.selectedToDate.getTime());

        const prefix = this.config.storagePrefix;
        localStorage.setItem(prefix + 'FromDate', this.fromDate.toISOString());
        localStorage.setItem(prefix + 'ToDate', this.toDate.toISOString());

        if (this.isPortfolio) {
            localStorage.setItem(prefix + 'IsEDR', this.isEDR.toString());
        }

        this.summaryRefreshKey++;
    }

    exportToExcel() {
        try {
            const workbook = XLSX.utils.book_new();
            const filterRow = this.fromDate && this.toDate
                ? moment(this.fromDate).format('YYYY-MM-DD') + ' - ' + moment(this.toDate).format('YYYY-MM-DD')
                : '';

            const mappedData = this.detailMapData({ results: this.lastDetailResults, ctrl: this });
            const headers = this.detailColumns.map((c: any) => c.label);
            const rows = mappedData.map((item: any) =>
                this.detailColumns.map((c: any) => {
                    if (c.type === 'html') return item.statusValue || '';
                    if (c.type === 'date') return item[c.key] ? moment(item[c.key]).format('YYYY-MM-DD') : '---';
                    return item[c.key] != null ? item[c.key] : '';
                })
            );
            const detailWsData = filterRow ? [[filterRow], [''], headers, ...rows] : [headers, ...rows];
            const detailWs = XLSX.utils.aoa_to_sheet(detailWsData);
            detailWs['!cols'] = this.detailColumns.map(() => ({ wch: 20 }));
            XLSX.utils.book_append_sheet(workbook, detailWs, this.config.exportSheetName);

            if (this.lastSummaryData) {
                const summaryRows = this.summaryLabels.map((l: any) => [
                    l.label,
                    this.lastSummaryData[l.key] != null ? this.lastSummaryData[l.key] : 0
                ]);
                const summaryWsData = filterRow
                    ? [[filterRow], [''], ['Metric', 'Value'], ...summaryRows]
                    : [['Metric', 'Value'], ...summaryRows];
                const summaryWs = XLSX.utils.aoa_to_sheet(summaryWsData);
                summaryWs['!cols'] = [{ wch: 30 }, { wch: 15 }];
                XLSX.utils.book_append_sheet(workbook, summaryWs, 'Summary');
            }

            const fromStr = this.fromDate ? moment(this.fromDate).format('YYYY-MM-DD') : '';
            const toStr = this.toDate ? moment(this.toDate).format('YYYY-MM-DD') : '';
            const fileName = this.config.exportFilePrefix +
                (fromStr && toStr ? '_' + fromStr + '_to_' + toStr : '') +
                '_' + moment().format('YYYY-MM-DD') + '.xlsx';

            XLSX.writeFile(workbook, fileName);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            this.notifyjs('Error exporting to Excel.', 'error');
        }
    }

    private buildSummaryLabels(): any[] {
        if (this.isPortfolio) {
            return [
                { key: 'totalOpened', label: 'Opened' },
                { key: 'totalClosed', label: 'Closed' },
                { key: 'totalReopened', label: 'Reopened' },
                { key: 'totalOpenedAndClosed', label: 'Opened & Closed' },
            ];
        }
        return [
            { key: 'totalOpened', label: 'New' },
            { key: 'totalClosed', label: 'Closed' },
            { key: 'totalReopened', label: 'Reopened' },
            { key: 'totalOpenedAndClosed', label: 'Opened & Closed' },
        ];
    }

    private buildSummaryFetchData(): (args: { fromDate: Date; toDate: Date }) => angular.IPromise<any> {
        return (args) => {
            const from = moment(args.fromDate).format('YYYY-MM-DD');
            const to = moment(args.toDate).format('YYYY-MM-DD');
            this.loadingCount++;

            const promise = this.isPortfolio
                ? this.feesRepo.getPortfolioActivitySummary(from, to, this.getAccountSource())
                : this.feesRepo.getClientActivitySummary(from, to);

            return promise.then((r: any) => {
                this.lastSummaryData = r && r.data ? r.data : null;
                return r;
            }).finally(() => {
                this.loadingCount--;
            });
        };
    }

    private buildDetailColumns(): any[] {
        if (this.isPortfolio) {
            return [
                { key: 'portfolioNumber', label: 'Portfolio Number', type: 'text' },
                { key: 'client', label: 'Client', type: 'text' },
                { key: 'status', label: 'Activity', type: 'html' },
                { key: 'openDate', label: 'Open Date', type: 'date' },
                { key: 'startDateFeeType', label: 'Fee start date', type: 'text' },
                { key: 'closeDate', label: 'Close Date', type: 'date' },
            ];
        }
        return [
            { key: 'clientNumber', label: 'Client Number', type: 'text' },
            { key: 'clientName', label: 'Client Name', type: 'text' },
            { key: 'status', label: 'Activity', type: 'html' },
            { key: 'earliestOpenDate', label: 'Earliest Open Date', type: 'date' },
            { key: 'latestCloseDate', label: 'Latest Close Date', type: 'date' },
        ];
    }

    private buildDetailStatusOptions(): any[] {
        if (this.isPortfolio) {
            return [
                { value: 'Opened', label: 'Opened' },
                { value: 'Closed', label: 'Closed' },
                { value: 'Reopened', label: 'Reopened' },
                { value: 'OpenedAndClosed', label: 'Opened & Closed' }
            ];
        }
        return [
            { value: 'New', label: 'New' },
            { value: 'Closed', label: 'Closed' },
            { value: 'Reopened', label: 'Reopened' },
            { value: 'OpenedAndClosed', label: 'Opened & Closed' }
        ];
    }

    private buildDetailFetchData(): (args: { fromDate: Date; toDate: Date }) => angular.IPromise<any> {
        return (args) => {
            const from = moment(args.fromDate).format('YYYY-MM-DD');
            const to = moment(args.toDate).format('YYYY-MM-DD');
            this.loadingCount++;

            const promise = this.isPortfolio
                ? this.feesRepo.getPortfolioActivityDetails(from, to, this.getAccountSource())
                : this.feesRepo.getClientActivityDetails(from, to);

            return promise.then((r: any) => {
                const results = r && r.data && r.data.results;
                this.lastDetailResults = results && Array.isArray(results) ? results : [];
                return r;
            }).finally(() => {
                this.loadingCount--;
            });
        };
    }

    private buildDetailMapData(): (args: { results: any[]; ctrl: any }) => any[] {
        if (this.isPortfolio) {
            return this.mapPortfolioData;
        }
        return this.mapClientData;
    }

    private mapClientData = (args: { results: any[]; ctrl: any }) => {
        return args.results.map((r: any) => {
            const statusValue = r.status != null ? r.status : r.Status;
            const clientNumber = r.clientNumber != null ? r.clientNumber : r.ClientNumber;
            const clientName = r.clientName != null ? r.clientName : r.ClientName;
            const earliestOpenDate = r.earliestOpenDate != null ? r.earliestOpenDate : (r.EarliestOpenDate != null ? r.EarliestOpenDate : null);
            const latestCloseDate = r.latestCloseDate != null ? r.latestCloseDate : (r.LatestCloseDate != null ? r.LatestCloseDate : null);

            let textClass = '';
            if (statusValue === 'New' || statusValue === 'Reopened') {
                textClass = 'text-success';
            } else if (statusValue === 'Closed') {
                textClass = 'text-danger';
            } else if (statusValue === 'OpenedAndClosed') {
                textClass = 'text-warning';
            }
            const displayStatus = statusValue === 'OpenedAndClosed' ? 'Opened & Closed' : statusValue;
            const statusHtml = textClass
                ? `<span class="${textClass}">${displayStatus}</span>`
                : `<span>${displayStatus}</span>`;

            return {
                statusValue,
                clientNumber,
                clientName,
                status: statusHtml,
                earliestOpenDate,
                latestCloseDate,
            };
        });
    };

    private mapPortfolioData = (args: { results: any[]; ctrl: any }) => {
        return args.results.map((r: any) => {
            const statusValue = r.status != null ? r.status : r.Status;
            const openDate = r.openDate != null ? r.openDate : (r.OpenDate != null ? r.OpenDate : null);
            const closeDate = r.closeDate != null ? r.closeDate : (r.CloseDate != null ? r.CloseDate : null);
            const isFirstAppearance = !!(r.isFirstAppearance != null ? r.isFirstAppearance : r.IsFirstAppearance);
            const portfolioNumber = r.portfolioNumber != null ? r.portfolioNumber : r.PortfolioNumber;
            const clientNumber = r.clientNumber != null ? r.clientNumber : r.ClientNumber;
            const clientName = r.clientName != null ? r.clientName : r.ClientName;
            const client = clientNumber && clientName
                ? (String(clientNumber) + " - " + String(clientName))
                : (clientNumber != null ? clientNumber : "");
            const startDateFeeType = r.startDateFeeType != null ? r.startDateFeeType : (r.StartDateFeeType != null ? r.StartDateFeeType : null);

            let displayStatus = statusValue;
            if (statusValue === "OpenedAndClosed") displayStatus = "Opened & Closed";

            let textClass = '';
            if (displayStatus === 'Reopened' || displayStatus === 'Opened') {
                textClass = 'text-success';
            } else if (displayStatus === 'Closed') {
                textClass = 'text-danger';
            } else if (displayStatus === 'Opened & Closed') {
                textClass = 'text-warning';
            }
            const statusHtml = textClass
                ? `<span class="${textClass}">${displayStatus}</span>`
                : `<span>${displayStatus}</span>`;

            return {
                statusValue,
                portfolioNumber,
                client,
                status: statusHtml,
                openDate,
                closeDate,
                isFirstAppearance,
                startDateFeeType,
            };
        });
    };
}

export const activityDashboard: angular.IComponentOptions = {
    template: require('./activity-dashboard.html'),
    controller: ActivityDashboardController,
    bindings: {
        mode: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/fees/fees-components/activity-dashboard/activity-dashboard.ts